import { useEffect, useRef, useState } from "react";
import { read, utils } from "xlsx";

export function useSingleExcelDragUpload<T>() {
  const { register, dragState, data } = useSingleDragUpload();
  const [excelData, setExcelData] = useState<T | null>(null);

  useEffect(() => {
    if (!data) {
      setExcelData(null);
      return;
    }

    const workbook = read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const obj = utils.sheet_to_json(sheet);

    setExcelData(obj as T);
  }, [data]);

  return {
    register,
    dragState,
    excelData,
  };
}

export function useMultiFileDragUpload() {
  const [dragState, setDragState] = useState<DragState>("leave");
  const [data, setData] = useState<FileList>();

  function dropHandler(ev: React.DragEvent<HTMLDivElement>) {
    setDragState("leave");

    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    if (ev.dataTransfer.files) {
      if (ev.dataTransfer.files.length < 1) return;
      setData(ev.dataTransfer.files);
    }
  }

  function dragOverHandler(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
  }

  return {
    register: () => {
      return {
        onDrop: dropHandler,
        onDragEnter: () => setDragState("enter"),
        onDragLeave: () => setDragState("leave"),
        onDragOver: dragOverHandler,
      };
    },
    dragState,
    data,
  };
}

export type DragState = "enter" | "leave";

export function useSingleDragUpload() {
  const [dragState, setDragState] = useState<DragState>("leave");
  const [data, setData] = useState<Uint8Array>();

  function dropHandler(ev: React.DragEvent<HTMLDivElement>) {
    setDragState("leave");

    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    if (ev.dataTransfer.items) {
      if (ev.dataTransfer.items.length !== 1) return;
      const item = [...ev.dataTransfer.items][0];

      if (item.kind !== "file") return;

      const file = item.getAsFile();
      if (file) {
        handleStream(file.stream());
      }
    } else {
      if (ev.dataTransfer.files.length !== 1) return;

      const file = [...ev.dataTransfer.files][0];
      handleStream(file.stream());
    }
  }

  async function handleStream(stream: ReadableStream<Uint8Array>) {
    setData(await readStream(stream));
  }

  function dragOverHandler(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
  }

  async function readStream(stream: ReadableStream<Uint8Array>) {
    /* collect data */
    const buffers: Uint8Array[] = [];
    const reader = stream.getReader();
    while (true) {
      const res = await reader.read();
      if (res.value) buffers.push(res.value);
      if (res.done) break;
    }

    /* concat */
    const size = buffers.reduce((acc, v) => acc + v.length, 0);
    const out = new Uint8Array(size);

    let off = 0;
    for (const u8 of buffers) {
      out.set(u8, off);
      off += u8.length;
    }

    return out;
  }

  return {
    register: () => {
      return {
        onDrop: dropHandler,
        onDragEnter: () => setDragState("enter"),
        onDragLeave: () => setDragState("leave"),
        onDragOver: dragOverHandler,
      };
    },
    dragState,
    data,
  };
}

export function useSingleExcelInputUpload<T>() {
  const { register, data } = useSingleInputUpload();
  const [excelData, setExcelData] = useState<T | null>(null);

  useEffect(() => {
    if (!data) {
      return setExcelData(null);
    }
    const workbook = read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const obj = utils.sheet_to_json(sheet);
    setExcelData(obj as T);
  }, [data]);

  return {
    register,
    excelData,
  };
}

export function useSingleInputUpload() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<ArrayBuffer>();

  function handleFile() {
    const files = inputRef.current?.files;

    if (!files) return;
    if (files.length === 0) return;

    const file = files[0];

    const fileReader = new FileReader();

    fileReader.onload = () => {
      setData(fileReader.result as ArrayBuffer);
    };
    fileReader.readAsArrayBuffer(file);
  }

  return {
    data,
    register: () => {
      return {
        ref: inputRef,
        onChange: handleFile,
      };
    },
  };
}
